import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';


interface UserProfile {
  userName: string;
  email: string;
  phoneNumber: string;
}

interface FeedbackData {
    id: number;
    feedbackContent: string;
    userId: number;
    subject: string;
    createdDate: string;
    userName: string;
    email: string;
    phoneNumber: number;
}

const FeedBack: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const navigate = useNavigate();
  const [feedbackContent, setFeedbackContent] = useState('');
  const [subject, setSubject] = useState('');

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false); // Modal visibility state
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const hasChanges = 
      feedbackContent !== '' || 
      subject !== '';
    setIsFormChanged(hasChanges);
  }, [feedbackContent, subject]);

  

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  // Fetch user profile data
  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token"); // Get the stored token
    if (!token) {
      // console.error('Token not found in localStorage');
      setErrorMessage('Session expired. Please log in again.');
      navigate('/home'); // Redirect to login page
      return; // Stop execution if token is not found
    }

    try {
      // console.log('Fetching user profile...');
      // console.log('Token:', token); // Log the token for debugging

      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/User/GetUserProfile', {
        method: 'GET',
        headers: {
          "Authorization": `Bearer ${token}`, // Add token to Authorization header
          "Content-Type": "application/json", // Ensure Content-Type is set
        },
      });

      // console.log('Response Status:', response.status); // Log the response status

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.status} ${response.statusText}`);
      }

      const data: UserProfile = await response.json();
      // console.log('Fetched User Profile:', data); // Log the fetched user profile
      setUserProfile(data); // Save user profile to state
      setUserName(data.userName); // Update the username state
      updateUserName(data.userName); // Update the username in the parent component


      setLoading(false);  // Set loading to false after fetching data
      // console.log("User profile data:", data);  // Log the fetched data
    } catch (error) {
      // console.error('Error fetching user profile:', error);
      toast.error("An error occurred while fetching user profile.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const handleLogout = () => {
    // Clear user session (localStorage, sessionStorage, or cookies)
    localStorage.removeItem('userToken'); // Example: remove the user token or session info
    sessionStorage.clear(); // Clear sessionStorage if needed
    // You can also clear cookies if used for session management

    // Redirect to the Register page
    navigate('/'); // Redirects to localhost:3000/register
  };
  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserProfile((prevProfile) => ({
        ...prevProfile,
        userName: storedUserName,
        email: prevProfile?.email || '',
        phoneNumber: prevProfile?.phoneNumber || '',
      } as UserProfile)); // Type assertion to ensure compatibility
    }

    const handleStorageChange = () => {
      const updatedUserName = localStorage.getItem('userName');
      if (updatedUserName) {
        setUserProfile((prevProfile) => ({
          ...prevProfile,
          userName: updatedUserName,
          email: prevProfile?.email || '',
          phoneNumber: prevProfile?.phoneNumber || '',
        } as UserProfile));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      navigate('/home');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/Feedback/AddFeedback', {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          feedbackContent: feedbackContent,
          subject: subject,
          
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status} ${response.statusText}`);
      }

      // toast.success("Feedback submitted successfully!");
      // console.log("new", response)
      setFeedbackContent('');
      setSubject('');
      setIsSubmitting(false);
    
    } catch (error) {
      toast.error("An error occurred while submitting feedback.");
      setIsSubmitting(false);
    }
  };

  

  // Add this skeleton loader component
  const FeedbackSkeleton = () => (
    <div className="w-full p-8 bg-white rounded-lg shadow-md animate-pulse">
      {/* Subject Field Skeleton */}
      <div className="mb-6">
        <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>

      {/* Feedback Field Skeleton */}
      <div className="mb-6">
        <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
        <div className="h-32 w-full bg-gray-200 rounded"></div>
      </div>

      {/* Buttons Skeleton */}
      <div className="flex items-center justify-end gap-4 mt-4">
        <div className="h-10 w-32 bg-gray-200 rounded"></div>
        <div className="h-10 w-20 bg-gray-200 rounded"></div>
      </div>
    </div>
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormChanged && !isSubmitting) {
        handleFeedbackSubmit(e as React.FormEvent);
    }
};

  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
      <header className='flex items-center fixed top-0 right-4 left-5 z-10 justify-between px-6 py-1 bg-white'>

        <h1 className='text-lg font-semibold'></h1>
        <div className="flex justify-end items-center gap-6">

          <div className="relative m-3">
            <button
              onClick={toggleDropdown}
              className="relative border border-whitesmoke focus:outline-none flex rounded-full h-12 items-center px-2 gap-2"
            >
              <div className="w-8 h-8 overflow-hidden rounded-full">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn-icons-png.flaticon.com/128/18573/18573514.png"
                  alt="User Avatar"
                />
              </div>
              <h1>{userName || ''}</h1>          </button>

            {dropdownOpen && (
              <>

                <div
                  onClick={() => setDropdownOpen(false)}
                  className="fixed inset-0 z-10 w-full h-full"
                ></div>

                <div className="absolute right-0 z-20 w-48 mt-2 bg-white rounded-md shadow-xl">
                  <button
                    onClick={() => {
                      closeDropdown();
                      navigate('/profile', {
                        state: {
                          userName: userProfile?.userName,
                          email: userProfile?.email,
                          phoneNumber: userProfile?.phoneNumber,
                        },
                      });
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Profile
                  </button>
                  {/* <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white">
          Products
        </a> */}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="p-6 flex-grow mt-6">
        <div className=" mt-6">
          <h1 className="text-xl font-bold mb-6 mt-12">Submit Feedback</h1>
        </div>

        {loading ? (
          <FeedbackSkeleton />
        ) : (
          <div className="w-full p-8 bg-white rounded-lg shadow-md">
            <form className="grid grid-cols-1 gap-4" onSubmit={handleFeedbackSubmit}>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  id="subject"
                  type="text"
                  value={subject}  // Bind value to state
                  placeholder="Write your subject here..."

                  onChange={(e) => setSubject(e.target.value)}  // Update state on input change
                  onKeyDown={handleKeyDown}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>


              <div className="col-span-3">
                <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback
                </label>
                <textarea
                  id="feedback"
                  value={feedbackContent}
                  onChange={(e) => setFeedbackContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Write your feedback here..."
                />
              </div>

              <div className="col-span-3 flex items-center justify-end gap-4 mt-4">
                <button
                  id="submit"
                  value={subject}
                  disabled={!isFormChanged || isSubmitting}
                  className={`py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${isFormChanged && !isSubmitting
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-gray-400 cursor-not-allowed'}`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
                <button
                  onClick={() => navigate('/home')}
                  className="py-2 px-4 bg-gray-300 text-gray-800 rounded-md shadow hover:bg-gray-400 focus:outline-none"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        )}
        <footer className="w-full mt-80">
          <div className="px-6 py-6 bg-white shadow-shadow1 rounded-md">
            <p className="text-center text-jetgrey text-sm">
            GET ME THERE. © 2024
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default FeedBack;
